import React, { useContext, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { ConditionalRendering } from '../../../../../components/common/conditional-rendering';
import { CurrencyInput, ValidationErrorMessage } from '../../../../../components/common/form';
import { INPUT_TYPES } from '../../../../../components/common/form/constants/fieldTypes';
import Tooltip from '../../../../../components/common/tooltip/Tooltip';
import { PlAN_LEVEL } from '../../../../../constants/enrollmentList/enrollmentList.constant';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import { StyledField } from '../../styles/EnrollmentSharedStyled';

const ColumnMaxStateSubsidy = ({ premium }: { premium: IEnrollmentPremiumDto }) => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { viewEditableFields } = usePermissions();
    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        setValue,
        formState: { errors, isValid },
    } = enrollmentPremiumFormContext;
    const values = watch();
    const { isEnrollmentPendingOrPendCancelled } = usePermissions();

    const onMaxStateSubsidyChange = e => {
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.maxStateSubsidy`, e.target.value);
    };

    const isDisabled = useMemo(() => {
        return (
            values.planLevel === PlAN_LEVEL.CATASTROPHIC ||
            !premium.isStateSubsidyEligible ||
            selectedEnrollment.isItf ||
            !userPermissions?.memberEnrlmntAptcEditable ||
            isEnrollmentPendingOrPendCancelled(selectedEnrollment)
        );
    }, []);

    return (
        <ConditionalRendering
            isTrue={selectedEnrollment.isStateSubsidyEnabled && viewEditableFields(userPermissions, values, premium)}
        >
            <StyledField>
                <CurrencyInput
                    // @ts-ignore
                    label={{
                        wrapperClasses: [''],
                        labelClasses: [''],
                        labelText: '',
                        showRequired: false,
                        isInputRequired: false,
                    }}
                    input={{
                        id: 'maxStateSubsidy',
                        isInputRequired: true,
                        type: INPUT_TYPES.TEXT,
                        value: premium.maxStateSubsidy,
                        disabled: isDisabled,
                        dataTestId: 'maxStateSubsidy',
                        name: 'maxStateSubsidy',
                        placeholder: '',
                    }}
                    actions={{
                        handleChange: e => onMaxStateSubsidyChange(e),
                    }}
                    maskOptions={{
                        allowDecimal: true,
                        prefix: '',
                        decimalLimit: '2',
                        integerLimit: 10,
                        includeThousandsSeparator: false,
                        allowLeadingZeroes: true,
                    }}
                    showIcon={true}
                />
                <ConditionalRendering isTrue={!premium.isFinancial}>
                    <Tooltip
                        id="questinMark"
                        elementId="groupMaxAPTCTooltip"
                        content="member.enrollment_list.premiumhistory.tooltip.maxStateSubsidy"
                        place="top"
                        icon="fa-solid fa-circle-question"
                        dataAutomationId="groupMaxAPTCTooltip"
                    />
                </ConditionalRendering>
            </StyledField>

            <ConditionalRendering isTrue={premium.isFinancial}>
                <ValidationErrorMessage
                    isVisible={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.maxStateSubsidy}
                    messageText={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.maxStateSubsidy?.message}
                />
            </ConditionalRendering>
        </ConditionalRendering>
    );
};

export default ColumnMaxStateSubsidy;
